/**
 * Plays the configured join/leave sounds for the conversation call or Space
 * voice channel this device is in, by diffing the participant sets the call
 * providers already maintain (so duplicate socket events never double-play).
 *
 * Mount once inside CallSessionProvider and VoiceChannelSessionProvider.
 */

import { useEffect, useMemo, useRef } from 'react';
import { usePlatformCapabilities } from '../../config';
import type { CallPresenceEvent } from '../../hooks/callPresenceSoundStorage';
import { useCallSession } from '../../hooks/useCallSession';
import { useFriends } from '../../hooks/useFriends';
import { useIdentity } from '../../hooks/useIdentity';
import { useCallPresenceSoundPreference } from '../../hooks/useNotificationSoundPreference';
import { useOptionalVoiceChannelSession } from '../../hooks/useVoiceChannelSession';
import {
  type CallPresenceSoundChoice,
  type CallPresenceState,
  activeParticipantIds,
  diffCallPresence,
  resolveCallPresenceBatchSound,
  resolveCallPresenceSound,
} from '../../utils/callPresenceSound';
import { previewNotificationSound } from '../../utils/notificationSound';

/** Mass joins in a busy voice channel play once rather than stacking. */
export const CALL_PRESENCE_SOUND_THROTTLE_MS = 300;

export function useCallPresenceSounds(): void {
  const { activeSession } = useCallSession();
  const voice = useOptionalVoiceChannelSession();
  const { friends } = useFriends();
  const { identity } = useIdentity();
  const { audio } = usePlatformCapabilities();
  const joinPrefs = useCallPresenceSoundPreference('join');
  const leavePrefs = useCallPresenceSoundPreference('leave');

  const friendIds = useMemo(() => new Set(friends.map((f) => f.identity.id)), [friends]);

  const callId = activeSession?.call.id;
  const callParticipants = activeSession?.call.participants;
  const callState = useMemo<CallPresenceState | null>(
    () =>
      callId && callParticipants
        ? { key: callId, activeIds: activeParticipantIds(callParticipants) }
        : null,
    [callId, callParticipants]
  );

  const joined = voice?.joined ?? null;
  const voiceKey = joined ? `${joined.spaceId}:${joined.channelId}` : null;
  const voiceParticipants = joined?.session.participants;
  const voiceState = useMemo<CallPresenceState | null>(
    () =>
      voiceKey && voiceParticipants
        ? { key: voiceKey, activeIds: activeParticipantIds(voiceParticipants) }
        : null,
    [voiceKey, voiceParticipants]
  );

  const latest = useRef({ joinPrefs, leavePrefs, friendIds, myId: identity?.id ?? null, audio });
  latest.current = { joinPrefs, leavePrefs, friendIds, myId: identity?.id ?? null, audio };

  const lastPlayedAt = useRef<Record<CallPresenceEvent, number>>({
    join: Number.NEGATIVE_INFINITY,
    leave: Number.NEGATIVE_INFINITY,
  });

  const playRef = useRef((event: CallPresenceEvent, choice: CallPresenceSoundChoice | null) => {
    if (!choice) return;
    const now = Date.now();
    if (now - lastPlayedAt.current[event] < CALL_PRESENCE_SOUND_THROTTLE_MS) return;
    lastPlayedAt.current[event] = now;
    void previewNotificationSound({
      ...choice,
      loadCustomSound: latest.current.audio?.loadSoundFromPath,
    }).catch(() => {});
  });

  const onChange = useRef((prev: CallPresenceState | null, next: CallPresenceState | null) => {
    const { joinPrefs: join, leavePrefs: leave, friendIds: friendSet, myId } = latest.current;
    const play = playRef.current;
    const diff = diffCallPresence(prev, next, myId);
    if (diff.selfJoined) play('join', resolveCallPresenceSound(join, { isSelf: true, isFriend: false }));
    if (diff.selfLeft) play('leave', resolveCallPresenceSound(leave, { isSelf: true, isFriend: false }));
    play('join', resolveCallPresenceBatchSound(join, diff.joined, friendSet));
    play('leave', resolveCallPresenceBatchSound(leave, diff.left, friendSet));
  });

  usePresenceDiff(callState, onChange.current);
  usePresenceDiff(voiceState, onChange.current);
}

function usePresenceDiff(
  state: CallPresenceState | null,
  onChange: (prev: CallPresenceState | null, next: CallPresenceState | null) => void
): void {
  const prevRef = useRef<CallPresenceState | null>(null);
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = state;
    if (prev !== state) onChange(prev, state);
  }, [state, onChange]);
}

export function CallPresenceSoundListener(): null {
  useCallPresenceSounds();
  return null;
}
